import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, Table, Input, Button, Space, Tag, Spin, Typography, message, Grid, List } from 'antd';
import { SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getPayablesAndReceivables } from '@/api/finance';
import { useAuth } from '@/contexts/AuthContext';
import { Payable } from '@/types/finance/payable';
import { Receivable } from '@/types/finance/receivable';
import { Receipt } from '@/types';
import dayjs from 'dayjs';
import { formatFirestoreTimestamp } from '@/utils/date';

const { Title } = Typography;

const Accounting: React.FC = () => {
  const { t } = useTranslation(['accounting', 'common']);
  const navigate = useNavigate();
  const { firestoreUser } = useAuth();
  const screens = Grid.useBreakpoint();
  const [searchTerm, setSearchTerm] = useState('');

  // State for pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  // Query for accounting records
  const { data, isLoading, error } = useQuery({
    queryKey: ['accounting', pagination.current, pagination.pageSize, searchTerm],
    queryFn: () => getPayablesAndReceivables({
      searchTerm,
      pageSize: pagination.pageSize,
      page: pagination.current,
      firestoreUser: firestoreUser!,
    }),
    enabled: !!firestoreUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const formatMoney = (amount: any) => {
    if (!amount || typeof amount !== 'object') return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: amount.currency || 'USD'
    }).format(amount.amount);
  };



  const getTypeTagColor = (type: string) => {
    return type === 'payable' ? 'red' : 'green';
  };

  const renderAccountingCard = (record: any) => (
    <Card
      key={record.key}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleView(record)}
        >
          {t('common:actions.view')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={record.receiptName}
        description={
          <Space direction="vertical" size="small">
            <div>
              <Tag color={getTypeTagColor(record.recordType)}>
                {t(`accounting:types.${record.recordType}`)}
              </Tag>
            </div>
            <div>
              <strong>{t('accounting:table.receiptAmount')}:</strong> {formatMoney(record.receiptAmount)}
            </div>
            <div>
              <strong>{t('accounting:table.receiptDate')}:</strong> {
                record.receiptDate ? 
                  dayjs(formatFirestoreTimestamp(record.receiptDate)).format('MMM DD, YYYY') :
                  '-'
              }
            </div>
            <div>
              <strong>{t('accounting:table.account')}:</strong> {record.accountName} ({record.accountType})
            </div>
          </Space>
        }
      />
    </Card>
  );

  // Transform data for table display
  const tableData = React.useMemo(() => {
    if (!data?.records) return [];

    const records: any[] = [];
    
    data.records.forEach((record: Payable | Receivable) => {
      const completedReceipts = record.receipts?.filter((r: Receipt) => r.status === 'completed') || [];
      
      completedReceipts.forEach((receipt: Receipt) => {
        const isPayable = 'vendor' in record || 'employee' in record || 'asset' in record;
        const type = isPayable ? 'payable' : 'receivable';
        
        let accountName = '-';
        let accountType = '-';
        
        if (isPayable) {
          const payable = record as Payable;
          if (payable.vendor) {
            accountName = payable.vendor.name;
            accountType = 'Vendor';
          } else if (payable.employee) {
            accountName = payable.employee.name;
            accountType = 'Employee';
          } else if (payable.asset) {
            accountName = payable.asset.name;
            accountType = 'Asset';
          }
        } else {
          const receivable = record as Receivable;
          if (receivable.resident) {
            accountName = receivable.resident.name;
            accountType = 'Resident';
          } else if (receivable.employee) {
            accountName = receivable.employee.name;
            accountType = 'Employee';
          } else if (receivable.vendor) {
            accountName = receivable.vendor.name;
            accountType = 'Vendor';
          }
        }

        records.push({
          key: `${record.id}_${receipt.id}`,
          recordId: record.id,
          recordType: type,
          recordName: record.name,
          receiptName: receipt.name,
          receiptAmount: receipt.amount,
          receiptDate: receipt.date,
          accountName,
          accountType,
          status: record.status,
          record
        });
      });
    });

    return records;
  }, [data]);

  const handleView = (record: any) => {
    navigate(`/finance/accounting/${record.recordId}`, {
      state: { record: record.record, type: record.recordType }
    });
  };

  const columns = [
    {
      title: t('accounting:table.receiptName'),
      dataIndex: 'receiptName',
      key: 'receiptName',
      render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: t('accounting:table.receiptAmount'),
      dataIndex: 'receiptAmount',
      key: 'receiptAmount',
      render: (amount: any) => (
        <span style={{ fontWeight: 500, color: '#52c41a' }}>
          {formatMoney(amount)}
        </span>
      ),
    },
    {
      title: t('accounting:table.receiptDate'),
      dataIndex: 'receiptDate',
      key: 'receiptDate',
      render: (date: any) => {
        if (!date) return '-';
        const formattedDate = formatFirestoreTimestamp(date);
        return dayjs(formattedDate).format('MMM DD, YYYY');
      },
    },
    {
      title: t('accounting:table.type'),
      dataIndex: 'recordType',
      key: 'recordType',
      render: (type: string) => (
        <Tag color={getTypeTagColor(type)}>
          {t(`accounting:types.${type}`)}
        </Tag>
      ),
    },
    {
      title: t('accounting:table.account'),
      dataIndex: 'accountName',
      key: 'accountName',
      render: (name: string, record: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            <a onClick={() => handleView(record)} style={{ color: '#1890ff', cursor: 'pointer' }}>
              {name}
            </a>
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.accountType}
          </div>
        </div>
      ),
    },
    {
      title: t('accounting:table.actions'),
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            {t('common:actions.view')}
          </Button>
        </Space>
      ),
    },
  ];

  if (error) {
    message.error(t('accounting:messages.error'));
  }

  return (
    <div style={{ width: '100%' }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={3}>{t('accounting:title')}</Title>
          <p style={{ color: '#666', marginBottom: 16 }}>
            {t('accounting:description')}
          </p>
          
          <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ marginBottom: 16 }}>
            <Input
              placeholder={t('common:search.placeholder')}
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: screens.xs ? '100%' : 300 }}
              allowClear
            />
          </Space>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>{t('accounting:messages.loading')}</div>
          </div>
        ) : screens.xs ? (
          <List
            dataSource={tableData}
            renderItem={renderAccountingCard}
            rowKey="key"
            locale={{
              emptyText: t('accounting:messages.noRecords'),
            }}
            grid={{ gutter: 16, xs: 1, sm: 1 }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: data?.total || 0,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              onChange: (page, pageSize) => {
                setPagination(prev => ({ 
                  ...prev, 
                  current: page, 
                  pageSize: pageSize || prev.pageSize 
                }));
              }
            }}
            locale={{
              emptyText: t('accounting:messages.noRecords'),
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default Accounting; 